// 멘션 입력창의 "표시 텍스트(@닉네임) ↔ 저장 토큰(<@id|이름>)" 변환과
// 엔티티({start,end,user_id,display}) 오프셋 관리를 담당하는 순수 유틸.
// 엔티티 범위 [start, end)는 표시 텍스트에서 "@display"가 차지하는 구간이다.
import { MENTION_TOKEN_REGEX, displayNameOf } from "utils/mention";

// 서버(Community BE)의 원문(토큰 포함) 기준 하드 제한 — validation.middleware.ts
export const SERVER_MAX_POST_CONTENT = 1000;
export const SERVER_MAX_COMMENT_CONTENT = 500;

// 토큰 원문 → { 표시 텍스트, 엔티티[] } (수정 모드 진입용)
export const parseTokensToDisplay = (raw) => {
  if (!raw) return { text: "", entities: [] };
  let text = "";
  const entities = [];
  let last = 0;
  raw.replace(MENTION_TOKEN_REGEX, (m, id, name, offset) => {
    text += raw.slice(last, offset);
    const start = text.length;
    text += `@${name}`;
    entities.push({ start, end: text.length, user_id: id, display: name });
    last = offset + m.length;
    return m;
  });
  text += raw.slice(last);
  return { text, entities };
};

// 표시 텍스트 + 엔티티 → 토큰 문자열 (제출용).
// 엔티티 범위의 실제 텍스트가 "@display"와 다르면(편집 등) 건너뛴다 — 사용자 텍스트 우선.
export const serializeDisplayToTokens = (text, entities) => {
  if (!entities || entities.length === 0) return text;
  const sorted = [...entities].sort((a, b) => b.start - a.start);
  let out = text;
  for (const e of sorted) {
    if (out.slice(e.start, e.end) !== `@${e.display}`) continue;
    out = `${out.slice(0, e.start)}<@${e.user_id}|${e.display}>${out.slice(e.end)}`;
  }
  return out;
};

// 두 문자열의 공통 prefix/suffix 디프 — textarea 편집은 항상 단일 연속 구간 치환이다.
export const computeChange = (prevText, nextText) => {
  if (prevText === nextText) return null;
  const pLen = prevText.length;
  const nLen = nextText.length;
  const minLen = Math.min(pLen, nLen);
  let p = 0;
  while (p < minLen && prevText[p] === nextText[p]) p += 1;
  let s = 0;
  while (s < minLen - p && prevText[pLen - 1 - s] === nextText[nLen - 1 - s]) s += 1;
  return { start: p, removedLen: pLen - p - s, insertedLen: nLen - p - s };
};

// 편집 구간과의 관계에 따라 엔티티를 이동/유지/해제한다.
// 앞(end <= start): 유지 / 뒤(start >= 편집끝): delta 이동 / 겹침: 해제(멘션 → 일반 텍스트).
export const adjustEntities = (entities, change) => {
  if (!change) return entities;
  const delta = change.insertedLen - change.removedLen;
  const editEnd = change.start + change.removedLen;
  const next = [];
  for (const e of entities) {
    if (e.end <= change.start) {
      next.push(e);
    } else if (e.start >= editEnd) {
      next.push({ ...e, start: e.start + delta, end: e.end + delta });
    }
    // 겹침 → 해제(drop)
  }
  return next;
};

// range(@쿼리)를 "@닉네임 "으로 치환하고 엔티티를 추가한다.
export const insertMentionDisplay = (text, entities, range, user) => {
  let name = String(displayNameOf(user)).replace(/[|>]/g, "");
  if (!name) {
    // 닉네임이 구분자로만 이루어진 경우 fallback (기존 토큰 삽입 규칙 이관)
    name = String(user.username || user.user_id).replace(/[|>]/g, "");
  }
  const display = `@${name}`;
  const inserted = `${display} `;
  const before = text.slice(0, range.start);
  const after = text.slice(range.end);
  const adjusted = adjustEntities(entities, {
    start: range.start,
    removedLen: range.end - range.start,
    insertedLen: inserted.length,
  });
  // 멘션 토큰 id는 public_id만 사용한다. 없으면 토큰 없이 표시 텍스트만 삽입한다.
  const entity = user.public_id
    ? { start: range.start, end: range.start + display.length, user_id: user.public_id, display: name }
    : null;
  const nextEntities = entity ? [...adjusted, entity].sort((a, b) => a.start - b.start) : adjusted;
  return { text: before + inserted + after, entities: nextEntities, caret: before.length + inserted.length };
};
